import React, { useCallback, useMemo } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useTranslation } from "providers/i18n"

import { useVenue } from "providers/venue"
import { getDurationDate } from "utils/date"
import { getOpeningText, isValidTime } from "utils/time"
import { replaceLineBreak } from "utils/string"
import dayjs from "config/dayjs"

import {
  Card,
  CardHeader,
  CardContent,
  CardMedia,
  List,
  Typography,
  useTheme,
} from "@mui/material"

import { ShareIconButton } from "components/Button"
import { CalendarIcon, LocationIcon } from "components/icons"
import { Head } from "components/Head"
import {
  InfoListItem,
  InfoListItemWithNavigation,
} from "../../components/ListItem"
import { LocationInfo } from "../../components/domains/features/LocationInfo"

export const EventDetail = () => {
  const params = useParams()
  const { eventId } = params
  const navigate = useNavigate()
  const { findFeatureById } = useVenue()
  const { t } = useTranslation()

  const theme = useTheme()
  const styleOverrides = theme.components[`VuiDetailCard`]?.styleOverrides || {}
  const {
    image,
    infoListItem,
    infoListItemWithNavigation,
    cardContent,
    cardHeader,
    cardContentPrimaryText,
    cardContentSecondaryText,
  } = styleOverrides

  const event = useMemo(
    () => findFeatureById(eventId),
    [eventId, findFeatureById]
  )

  const navigateToUnit = useCallback(
    (feature_id) => {
      navigate(`/maps/place/${feature_id}`)
    },
    [navigate]
  )

  const descriptionText = useMemo(
    () => replaceLineBreak(t(event?.properties?.description)),
    [t, event?.properties?.description]
  )

  if (!event?.properties) return null

  const {
    name,
    cover_image,
    start_date,
    end_date,
    daily_start_time,
    daily_end_time,
    feature_id: location_id,
    feature: location,
  } = event.properties

  const startTime = isValidTime(daily_start_time)
    ? daily_start_time.slice(0, 5)
    : null
  const endTime = isValidTime(daily_end_time)
    ? daily_end_time.slice(0, 5)
    : null

  const startDate = dayjs(start_date)
  const endDate = dayjs(end_date)

  const openingText = getOpeningText(startTime, endTime, t)

  return (
    <>
      <Head title={`${t(name)} (${t("Event Detail")})`} />
      <Card elevation={0}>
        <CardMedia component="img" sx={image} src={cover_image?.url} />
        <CardHeader
          title={t(name)}
          action={<ShareIconButton url={window.location.href} />}
          sx={cardHeader}
        />
        <List>
          <InfoListItem
            sx={infoListItem}
            icon={<CalendarIcon />}
            primary={getDurationDate(startDate, endDate)}
            secondary={openingText}
          />
          {location && (
            <InfoListItemWithNavigation
              sx={infoListItemWithNavigation}
              icon={<LocationIcon />}
              primary={t("Navigate")}
              secondary={<LocationInfo feature={event} component="span" />}
              onClick={(e) => navigateToUnit(location_id)}
            />
          )}
        </List>
        <CardContent sx={cardContent}>
          <Typography variant="body1" gutterBottom sx={cardContentPrimaryText}>
            {t("Event Detail")}
          </Typography>
          <Typography
            variant="body2"
            sx={cardContentSecondaryText}
            dangerouslySetInnerHTML={{
              __html: descriptionText,
            }}
          ></Typography>
        </CardContent>
      </Card>
    </>
  )
}
